import { useState, useEffect } from "react";
import { formatDateOnly } from "@/lib/date-only";
import { createFileRoute, useNavigate, useSearch, Link } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from "@/components/ui/alert-dialog";
import {
  Users,
  FileText,
  Clock,
  CheckCircle2,
  XCircle,
  ChevronDown,
  ChevronUp,
  Loader2,
  Ban,
  CalendarIcon,
  Search,
  AlertTriangle,
  RefreshCw,
  Award,
  StickyNote,
  Copy,
} from "lucide-react";
import { apiGet, apiPatch, apiPost } from "@/lib/api";
import { authClient } from "@/lib/auth-client";
import { Textarea } from "@/components/ui/textarea";
import { Fragment } from "react";
import { cn } from "@/lib/utils";
import { MessageText } from "@/components/ui/message-text";
import { QuizAssignmentSettings, DEFAULT_QUIZ_CHOICE, normalizeQuizChoice, type QuizChoice } from "@/components/admin/quiz-assignment-settings";
import { SubWorkerPicker } from "@/components/subs/sub-worker-picker";
import { PickerSearch, matchesQuery } from "@/components/admin/picker-search";
import { useUserFilter, userFilterSearch } from "@/lib/user-filter";
import { UserFilterChip } from "@/components/admin/user-filter-chip";

export interface AssignmentsSearch {
  open?: string;
  tab?: "certifications";
  assignmentId?: string;
  userId?: string;
  userName?: string;
  /** With userId: open the assign form for that person (Records quick action). */
  assign?: "certification" | "document";
}

/** Shared by /admin/assignments and the supervisor's own /supervisor page. */
export function validateAssignmentsSearch(search: Record<string, unknown>): AssignmentsSearch {
  return {
    open: typeof search.open === "string" ? search.open : undefined,
    tab: search.tab === "certifications" ? "certifications" : undefined,
    assignmentId: typeof search.assignmentId === "string" ? search.assignmentId : undefined,
    ...userFilterSearch(search),
    assign: search.assign === "certification" || search.assign === "document" ? search.assign : undefined,
  };
}

export const Route = createFileRoute("/_authenticated/admin/assignments")({
  component: AssignmentsPage,
  validateSearch: validateAssignmentsSearch,
});

/** A supervisor (not an admin or division admin) may assign to named workers only. */
function useIsPlainSupervisor(): boolean {
  const { data: session } = authClient.useSession();
  const role = session?.user?.role;
  return !!role && role !== "admin" && role !== "div_admin";
}

/** Pre-selected certification and workers, e.g. from the supervisor's "who needs a certification?" view. */
export interface CertAssignPrefill {
  certTypeId: string | null;
  workerIds: string[];
}

/**
 * Divisions the current user can target when assigning. Admins see every
 * division; a supervisor only the ones they supervise (the server enforces
 * the same limit -- this just keeps the picker honest).
 */
function useAssignableDivisions(enabled: boolean): Division[] {
  const { data: session } = authClient.useSession();
  const isAdmin = session?.user?.role === "admin" || session?.user?.role === "div_admin";
  const { data: adminData } = useQuery({
    queryKey: ["admin-divisions"],
    queryFn: () => apiGet<{ data: Division[] }>("/api/admin/divisions"),
    enabled: enabled && isAdmin,
  });
  const { data: allData } = useQuery({
    queryKey: ["divisions-basic"],
    queryFn: () => apiGet<{ data: Division[] }>("/api/divisions"),
    enabled: enabled && !isAdmin,
  });
  const { data: me } = useQuery({
    queryKey: ["me"],
    queryFn: () => apiGet<{ supervisorDivisionIds?: string[] }>("/api/me"),
    enabled: enabled && !isAdmin,
  });
  if (isAdmin) return adminData?.data ?? [];
  const mine = new Set(me?.supervisorDivisionIds ?? []);
  return (allData?.data ?? []).filter((d) => mine.has(d.id));
}

// ── Top-level page: Documents | Certifications tab switcher ─────────────────
export function AssignmentsPage({
  prefill,
  onPrefillUsed,
}: {
  prefill?: CertAssignPrefill | null;
  onPrefillUsed?: () => void;
} = {}) {
  const { tab: initialTab, assign } = useSearch({ strict: false }) as AssignmentsSearch;
  const userFilter = useUserFilter();
  const navigate = useNavigate();
  // Quick action from a user's Records: open the assign form for that person.
  const quickPerson = assign && userFilter.userId ? userFilter.userId : null;
  const quickCert: CertAssignPrefill | null = quickPerson && assign === "certification" ? { certTypeId: null, workerIds: [quickPerson] } : null;
  const quickDocUsers = quickPerson && assign === "document" ? [quickPerson] : undefined;
  const clearAssign = () =>
    navigate({ to: ".", search: ((prev: Record<string, unknown>) => ({ ...prev, assign: undefined })) as never, replace: true });
  const [tab, setTab] = useState<"documents" | "certifications">(prefill || quickCert ? "certifications" : assign === "document" ? "documents" : initialTab ?? "documents");

  return (
    <div className="mx-auto max-w-4xl space-y-4 p-4 pb-16">
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-bold">Assignments</h1>
      </div>

      {userFilter.userId && <UserFilterChip name={userFilter.userName} onClear={userFilter.clear} />}

      <div className="flex gap-1 rounded-lg border border-border p-1 bg-muted/30 w-fit">
        {[
          { id: "documents" as const, label: "Documents", icon: FileText },
          { id: "certifications" as const, label: "Certifications", icon: Award },
        ].map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            type="button"
            onClick={() => setTab(id)}
            className={cn(
              "flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors",
              tab === id ? "bg-surface-raised text-text-primary shadow-sm" : "text-muted-foreground hover:text-text-primary"
            )}
          >
            <Icon className="h-3.5 w-3.5" />
            {label}
          </button>
        ))}
      </div>

      {tab === "documents" ? (
        <DocumentAssignmentsView prefillUserIds={quickDocUsers} onPrefillUsed={clearAssign} />
      ) : (
        <CertAssignmentsView
          prefill={prefill ?? quickCert}
          onPrefillUsed={() => { onPrefillUsed?.(); clearAssign(); }}
        />
      )}
    </div>
  );
}

// ── Types ──────────────────────────────────────────────────────────────────
interface AssignmentSummary {
  id: string;
  documentId: string;
  docTitle: string;
  assignerName: string;
  assignedBy: string;
  assignedAt: string;
  dueDate: string | null;
  message: string | null;
  status: "active" | "cancelled" | "expired";
  expiredAt?: string | null;
  targets: { type: string; value: string | null; label?: string }[];
  stats: {
    total: number;
    approved: number;
    submitted: number;
    pending: number;
    hasQuizLocked?: boolean;
  };
}

interface AssignmentDetail {
  id: string;
  documentId: string;
  docTitle: string;
  assignerName: string;
  assignedBy: string;
  assignedAt: string;
  dueDate: string | null;
  message: string | null;
  status: "active" | "cancelled";
  targets: { type: string; value: string | null; label: string }[];
  completions: {
    userId: string;
    ackId: string;
    userName: string;
    userEmail: string;
    completionStatus: "incomplete" | "signed" | "awaiting_approval" | "approved";
    quizLocked: boolean;
    quizAttempts: number;
    submittedAt: string | null;
    approvedAt: string | null;
    userNote: string | null;
    adminNote: string | null;
  }[];
}

// ── Helpers ────────────────────────────────────────────────────────────────
function statusBadge(status: string) {
  if (status === "cancelled") return <Badge variant="secondary">Cancelled</Badge>;
  if (status === "expired") return <Badge className="bg-amber-500/15 text-amber-700 dark:text-amber-400 hover:bg-amber-500/20">Expired</Badge>;
  return <Badge className="bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 hover:bg-emerald-500/20">Active</Badge>;
}

function completionBadge(s: string) {
  const map: Record<string, { label: string; cls: string }> = {
    approved: { label: "Approved", cls: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400" },
    awaiting_approval: { label: "Awaiting Approval", cls: "bg-blue-500/15 text-blue-700 dark:text-blue-400" },
    signed: { label: "Signed", cls: "bg-violet-500/15 text-violet-700 dark:text-violet-400" },
    incomplete: { label: "Incomplete", cls: "bg-orange-500/15 text-orange-700 dark:text-orange-400" },
  };
  const v = map[s] ?? map.incomplete;
  return (
    <span className={`inline-flex text-[11px] font-medium px-2 py-0.5 rounded-full ${v.cls}`}>
      {v.label}
    </span>
  );
}

function formatTargets(targets: { type: string; value: string | null; label?: string }[]) {
  if (!targets.length) return "—";
  if (targets.some((t) => t.type === "all")) return "All Team";
  return targets.map((t) => t.label ?? t.value ?? t.type).join(", ");
}

const TARGET_TYPE_LABEL: Record<string, string> = {
  all: "All Team",
  role: "Role",
  division: "Division",
  job_title: "Job Title",
  user: "User",
};

// ── Document Assignments (existing view) ─────────────────────────────────────
function DocumentAssignmentsView({ prefillUserIds, onPrefillUsed }: { prefillUserIds?: string[]; onPrefillUsed?: () => void } = {}) {
  const { data: session } = authClient.useSession();
  const user = session?.user;
  const qc = useQueryClient();

  const isSuperAdmin = user?.role === "admin";
  const userFilter = useUserFilter();

  const [statusFilter, setStatusFilter] = useState<"active" | "cancelled" | "all">("active");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const { open: openId } = useSearch({ strict: false }) as AssignmentsSearch;
  const [detailId, setDetailId] = useState<string | null>(openId ?? null);
  useEffect(() => { if (openId) setDetailId(openId); }, [openId]);
  const [cancelTarget, setCancelTarget] = useState<{ id: string; title: string } | null>(null);
  const [approveTarget, setApproveTarget] = useState<{ assignmentId: string; userId: string; userName: string } | null>(null);
  const [cancelPersonTarget, setCancelPersonTarget] = useState<{ assignmentId: string; userId: string; userName: string } | null>(null);

  const params = new URLSearchParams({ page: String(page), limit: "20" });
  if (statusFilter !== "all") params.set("status", statusFilter);
  if (dateFrom) params.set("from", dateFrom);
  if (dateTo) params.set("to", dateTo);
  if (search) params.set("q", search);
  if (userFilter.userId) params.set("userId", userFilter.userId);

  const { data: listData, isLoading } = useQuery({
    queryKey: ["admin", "doc-assignments", statusFilter, dateFrom, dateTo, search, page, userFilter.userId],
    queryFn: () => apiGet<{ data: AssignmentSummary[]; total: number; page: number; totalPages: number }>(`/api/admin/doc-assignments?${params}`),
    staleTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
    refetchInterval: 30_000,
  });

  const { data: detailData } = useQuery({
    queryKey: ["admin", "doc-assignment-detail", detailId],
    queryFn: () => apiGet<{ data: AssignmentDetail }>(`/api/admin/doc-assignments/${detailId}`),
    staleTime: 0,
    refetchOnMount: "always",
    enabled: !!detailId,
  });

  const cancelMut = useMutation({
    mutationFn: (id: string) => apiPatch(`/api/admin/doc-assignments/${id}/cancel`, {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "doc-assignments"] });
      if (detailId) qc.invalidateQueries({ queryKey: ["admin", "doc-assignment-detail", detailId] });
      toast.success("Assignment cancelled");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const approveMut = useMutation({
    mutationFn: ({ assignmentId, userId }: { assignmentId: string; userId: string }) =>
      apiPatch(`/api/admin/doc-assignments/${assignmentId}/completions/${userId}/approve`, {}),
    onSuccess: () => {
      if (detailId) qc.invalidateQueries({ queryKey: ["admin", "doc-assignment-detail", detailId] });
      qc.invalidateQueries({ queryKey: ["admin", "doc-assignments"] });
      toast.success("Completion approved");
    },
    onError: (e: Error) => {
      toast.error(e.message);
      if (detailId) qc.invalidateQueries({ queryKey: ["admin", "doc-assignment-detail", detailId] });
      qc.invalidateQueries({ queryKey: ["admin", "doc-assignments"] });
    },
  });

  const cancelPersonMut = useMutation({
    mutationFn: ({ assignmentId, userId }: { assignmentId: string; userId: string }) =>
      apiPatch(`/api/admin/doc-assignments/${assignmentId}/completions/${userId}/cancel`, {}),
    onSuccess: () => {
      if (detailId) qc.invalidateQueries({ queryKey: ["admin", "doc-assignment-detail", detailId] });
      qc.invalidateQueries({ queryKey: ["admin", "doc-assignments"] });
      toast.success("Assignment cancelled for this person");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const reassignMut = useMutation({
    mutationFn: ({ assignmentId, userIds }: { assignmentId: string; userIds: string[] }) =>
      apiPost(`/api/admin/doc-assignments/${assignmentId}/reassign`, { userIds }),
    onSuccess: () => {
      if (detailId) qc.invalidateQueries({ queryKey: ["admin", "doc-assignment-detail", detailId] });
      toast.success("Users reset for re-signing");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const unlockQuizMut = useMutation({
    mutationFn: ({ assignmentId, userId }: { assignmentId: string; userId: string }) =>
      apiPost(`/api/admin/doc-assignments/${assignmentId}/completions/${userId}/unlock-quiz`, {}),
    onSuccess: () => {
      if (detailId) qc.invalidateQueries({ queryKey: ["admin", "doc-assignment-detail", detailId] });
      qc.invalidateQueries({ queryKey: ["admin", "doc-assignments"] });
      qc.invalidateQueries({ queryKey: ["my-notifications"] });
      toast.success("Quiz unlocked — user can try again");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const syncCrewMut = useMutation({
    mutationFn: (assignmentId: string) =>
      apiPost<{ added: number }>(`/api/admin/doc-assignments/${assignmentId}/sync-crew`, {}),
    onSuccess: (res) => {
      if (detailId) qc.invalidateQueries({ queryKey: ["admin", "doc-assignment-detail", detailId] });
      qc.invalidateQueries({ queryKey: ["admin", "doc-assignments"] });
      const count = res.added ?? 0;
      toast.success(count > 0 ? `${count} crew member${count !== 1 ? "s" : ""} added` : "Crew is already up to date");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const list = listData?.data ?? [];
  const isDone = (a: { status: string; stats: { total: number; approved: number } }) =>
    a.status === "active" && a.stats.total > 0 && a.stats.approved >= a.stats.total;
  const [showDone, setShowDone] = useState(true);
  const [showCancelled, setShowCancelled] = useState(false);
  const cancelledList = list.filter((a) => a.status === "cancelled");
  const activeList = list.filter((a) => a.status !== "cancelled" && !isDone(a));
  const doneList = list.filter(isDone);
  const visibleList = [...activeList, ...(showDone ? doneList : []), ...(showCancelled || statusFilter === "cancelled" ? cancelledList : [])];
  const firstDoneId = doneList[0]?.id;
  const firstCancelledId = cancelledList[0]?.id;
  const total = listData?.total ?? 0;
  const totalPages = listData?.totalPages ?? 1;
  const detail = detailData?.data ?? null;
  const [createOpen, setCreateOpen] = useState(!!prefillUserIds?.length);
  const [cleanupOpen, setCleanupOpen] = useState(false);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs text-muted-foreground">{total} total</p>
        <div className="flex flex-wrap items-center gap-2">
          {session?.user?.role === "admin" && (
            <Button size="sm" variant="outline" onClick={() => setCleanupOpen(true)}>
              <Copy className="h-4 w-4 mr-1.5" /> Clean up duplicates
            </Button>
          )}
          <Button size="sm" onClick={() => setCreateOpen(true)}>
            <FileText className="h-4 w-4 mr-1.5" /> Assign Document
          </Button>
        </div>
      </div>
      {cleanupOpen && <DuplicatesCleanupDialog basePath="/api/admin/doc-assignments" queryKey={["admin", "doc-assignments"]} onClose={() => setCleanupOpen(false)} onDone={() => qc.invalidateQueries({ queryKey: ["admin", "doc-assignments"] })} />}

      {/* Filters */}
      <div className="flex flex-wrap gap-2 items-end">
        <div className="relative flex-1 min-w-[160px]">
          <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
          <Input
            placeholder="Search document…"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="pl-8 h-8 text-xs"
          />
        </div>
        <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v as typeof statusFilter); setPage(1); }}>
          <SelectTrigger className="h-8 w-32 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
            <SelectItem value="expired">Expired</SelectItem>
            <SelectItem value="all">All</SelectItem>
          </SelectContent>
        </Select>
        <div className="flex items-center gap-1">
          <Label className="text-xs text-muted-foreground whitespace-nowrap">From</Label>
          <Input type="date" value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setPage(1); }} className="h-8 text-xs w-32" />
        </div>
        <div className="flex items-center gap-1">
          <Label className="text-xs text-muted-foreground whitespace-nowrap">To</Label>
          <Input type="date" value={dateTo} onChange={(e) => { setDateTo(e.target.value); setPage(1); }} className="h-8 text-xs w-32" />
        </div>
        {(dateFrom || dateTo || search) && (
          <Button variant="ghost" size="sm" className="h-8 text-xs" onClick={() => { setDateFrom(""); setDateTo(""); setSearch(""); setPage(1); }}>
            Clear
          </Button>
        )}
      </div>

      {/* List */}
      {isLoading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
      ) : list.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center py-12 text-center text-muted-foreground">
            <FileText className="h-10 w-10 mb-3 opacity-20" />
            <p className="text-sm">No assignments found</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {visibleList.map((a) => {
            const pct = a.stats.total > 0 ? Math.round((a.stats.approved / a.stats.total) * 100) : 0;
            const needsAttention = a.status === "active" && (!!a.stats.hasQuizLocked || a.stats.submitted > 0);
            return (
              <Fragment key={a.id}>
              {a.id === firstCancelledId && (
                <button
                  type="button"
                  onClick={() => setShowCancelled((v) => !v)}
                  className="flex w-full items-center justify-between pt-5 pb-1 text-left"
                  aria-expanded={showCancelled}
                >
                  <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Cancelled ({cancelledList.length})</span>
                  <span className="text-xs text-muted-foreground">Hide</span>
                </button>
              )}
              {a.id === firstDoneId && (
                <button
                  type="button"
                  onClick={() => setShowDone((v) => !v)}
                  className="flex w-full items-center justify-between pt-5 pb-1 text-left"
                  aria-expanded={showDone}
                >
                  <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Completed ({doneList.length})</span>
                  <span className="text-xs text-muted-foreground">{showDone ? "Hide" : "Show"}</span>
                </button>
              )}
              <Card
                key={a.id}
                className={`cursor-pointer transition-colors ${needsAttention ? "border-orange-400/60 bg-orange-500/5 hover:bg-orange-500/10" : "hover:bg-muted/30"}`}
                onClick={() => setDetailId(a.id)}
              >
                <CardContent className="px-4 py-3">
                  <div className="flex items-start gap-3">
                    <FileText className={`h-5 w-5 mt-0.5 shrink-0 ${needsAttention ? "text-orange-500/70" : "text-muted-foreground/50"}`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-sm font-medium truncate">{a.docTitle}</p>
                        {statusBadge(a.status)}
                        {a.stats.hasQuizLocked && (
                          <span className="inline-flex items-center gap-1 text-[10px] font-bold bg-orange-500/15 text-orange-600 dark:text-orange-400 px-1.5 py-0.5 rounded-full">
                            <AlertTriangle className="h-2.5 w-2.5" />Quiz Locked
                          </span>
                        )}
                        {a.stats.submitted > 0 && (
                          <span className="inline-flex items-center gap-1 text-[10px] font-bold bg-amber-500/15 text-amber-600 dark:text-amber-400 px-1.5 py-0.5 rounded-full">
                            <Clock className="h-2.5 w-2.5" />{a.stats.submitted} Awaiting
                          </span>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1">
                        <p className="text-xs text-muted-foreground">
                          By {a.assignerName} · {new Date(a.assignedAt).toLocaleDateString()}
                        </p>
                        {a.dueDate && (
                          <p className="text-xs text-muted-foreground flex items-center gap-1">
                            <CalendarIcon className="h-3 w-3" /> Due {formatDateOnly(a.dueDate)}
                          </p>
                        )}
                        <p className="text-xs text-muted-foreground flex items-center gap-1">
                          <Users className="h-3 w-3" /> {formatTargets(a.targets)}
                        </p>
                      </div>
                      {a.message && (
                        <p className="text-xs text-muted-foreground italic mt-1 truncate">"{a.message}"</p>
                      )}
                      {/* Progress bar */}
                      {a.stats.total > 0 && (
                        <div className="mt-2 flex items-center gap-2">
                          <div className="flex-1 h-1.5 rounded-full bg-muted overflow-hidden">
                            <div
                              className="h-full rounded-full bg-emerald-500 transition-all"
                              style={{ width: `${pct}%` }}
                            />
                          </div>
                          <span className="text-[11px] text-muted-foreground whitespace-nowrap">
                            {a.stats.approved}/{a.stats.total} approved · {a.stats.submitted} awaiting · {a.stats.pending} pending
                          </span>
                        </div>
                      )}
                    </div>
                    {a.status === "active" && a.stats.approved < a.stats.total && (isSuperAdmin || a.assignedBy === user?.id) && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0 text-destructive hover:text-destructive shrink-0"
                        title="Cancel assignment"
                        onClick={(e) => { e.stopPropagation(); setCancelTarget({ id: a.id, title: a.docTitle }); }}
                      >
                        <Ban className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
              </Fragment>
            );
          })}
          {doneList.length > 0 && !showDone && (
            <button
              type="button"
              onClick={() => setShowDone(true)}
              className="flex w-full items-center justify-between pt-5 pb-1 text-left"
            >
              <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Completed ({doneList.length})</span>
              <span className="text-xs text-muted-foreground">Show</span>
            </button>
          )}
          {cancelledList.length > 0 && !showCancelled && statusFilter !== "cancelled" && (
            <button
              type="button"
              onClick={() => setShowCancelled(true)}
              className="flex w-full items-center justify-between pt-5 pb-1 text-left"
            >
              <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Cancelled ({cancelledList.length})</span>
              <span className="text-xs text-muted-foreground">Show</span>
            </button>
          )}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center gap-2">
          <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage((p) => p - 1)}>Previous</Button>
          <span className="text-xs text-muted-foreground self-center">Page {page} of {totalPages}</span>
          <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Next</Button>
        </div>
      )}

      {/* Detail sheet */}
      {detailId && (
        <Sheet open onOpenChange={(v) => !v && setDetailId(null)}>
          <SheetContent side="right" className="w-full sm:max-w-lg flex flex-col">
            {!detail ? (
              <div className="flex-1 flex items-center justify-center">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <>
                <div className="flex-1 min-h-0 overflow-y-auto pr-1">
                <SheetHeader>
                  <SheetTitle className="truncate">{detail.docTitle}</SheetTitle>
                  <p className="text-xs text-muted-foreground">
                    Assigned by {detail.assignerName} · {new Date(detail.assignedAt).toLocaleDateString()}
                    {detail.dueDate ? ` · Due ${formatDateOnly(detail.dueDate)}` : ""}
                  </p>
                </SheetHeader>
                {detail.message && (
                  <NoteBox text={detail.message} />
                )}
                {detail.targets.length > 0 && (
                  <div className="mt-3">
                    <p className="text-xs text-muted-foreground font-medium mb-1.5">Assigned to</p>
                    <div className="flex flex-wrap gap-1.5">
                      {detail.targets.map((t, i) => (
                        <span
                          key={i}
                          className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full bg-muted text-muted-foreground"
                        >
                          {t.type !== "all" && t.type !== "user" && (
                            <span className="opacity-60">{TARGET_TYPE_LABEL[t.type] ?? t.type}:</span>
                          )}
                          {t.label ?? t.value ?? "—"}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex items-center justify-between mt-4 mb-1">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Crew ({detail.completions.length})</p>
                </div>
                <div className="space-y-2">
                  {detail.completions.map((c) => (
                    <div key={c.userId} className="flex items-center gap-2 py-2 border-b border-border/40 last:border-0">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{c.userName}</p>
                        <p className="text-[11px] text-muted-foreground">{c.userEmail}</p>
                        {c.userNote && (
                          <p className="text-[11px] text-muted-foreground mt-0.5 italic">"{c.userNote}"</p>
                        )}
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0 flex-wrap justify-end">
                        {c.quizLocked && (
                          <span className="inline-flex text-[11px] font-medium px-2 py-0.5 rounded-full bg-red-500/15 text-red-700 dark:text-red-400">
                            Quiz Locked
                          </span>
                        )}
                        {!c.quizLocked && completionBadge(c.completionStatus)}
                        {c.quizLocked && detail.status === "active" && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-6 text-[11px] px-2 text-blue-600 border-blue-300 hover:bg-blue-50"
                            onClick={() => unlockQuizMut.mutate({ assignmentId: detail.id, userId: c.userId })}
                            disabled={unlockQuizMut.isPending}
                          >
                            Unlock Quiz
                          </Button>
                        )}
                        {(c.completionStatus === "incomplete") && detail.status === "active" && (isSuperAdmin || detail.assignedBy === user?.id) && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-6 text-[11px] px-2 text-destructive border-destructive/40 hover:bg-destructive/10"
                            title="Cancel this assignment for this person only"
                            onClick={() => setCancelPersonTarget({ assignmentId: detail.id, userId: c.userId, userName: c.userName })}
                            disabled={cancelPersonMut.isPending}
                          >
                            Cancel
                          </Button>
                        )}
                        {!c.quizLocked && c.completionStatus === "awaiting_approval" && detail.status === "active" && (
                          <Button
                            size="sm"
                            className="h-6 text-[11px] px-2"
                            onClick={() => setApproveTarget({ assignmentId: detail.id, userId: c.userId, userName: c.userName })}
                            disabled={approveMut.isPending}
                          >
                            Approve
                          </Button>
                        )}
                        {(c.completionStatus === "signed" || c.completionStatus === "approved") && detail.status === "active" && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-6 text-[11px] px-2 text-amber-600 border-amber-300 hover:bg-amber-50"
                            title="Reset for re-signing"
                            onClick={() => reassignMut.mutate({ assignmentId: detail.id, userIds: [c.userId] })}
                            disabled={reassignMut.isPending}
                          >
                            Re-sign
                          </Button>
                        )}
                        {c.quizAttempts > 0 && !c.quizLocked && (
                          <span className="text-[10px] text-muted-foreground">
                            {c.quizAttempts} attempt{c.quizAttempts !== 1 ? "s" : ""}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                  {detail.completions.length === 0 && (
                    <p className="text-sm text-muted-foreground text-center py-8">No completions yet</p>
                  )}
                </div>

                </div>

                {detail.status === "active" && detail.completions.some((c) => c.completionStatus !== "approved") && (isSuperAdmin || detail.assignedBy === user?.id) && (
                  <div className="pt-4 border-t space-y-2">
                    {isSuperAdmin && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full text-muted-foreground"
                        onClick={() => syncCrewMut.mutate(detail.id)}
                        disabled={syncCrewMut.isPending}
                        title="Re-check who should be assigned and add any crew who were missed"
                      >
                        <RefreshCw className={`h-4 w-4 mr-2 ${syncCrewMut.isPending ? "animate-spin" : ""}`} /> Sync Crew
                      </Button>
                    )}
                    <Button
                      variant="destructive"
                      size="sm"
                      className="w-full"
                      onClick={() => setCancelTarget({ id: detail.id, title: detail.docTitle })}
                      disabled={cancelMut.isPending}
                    >
                      <Ban className="h-4 w-4 mr-2" /> Cancel Assignment
                    </Button>
                  </div>
                )}
              </>
            )}
          </SheetContent>
        </Sheet>
      )}

      {/* Cancel confirm */}
      <AlertDialog open={!!cancelTarget} onOpenChange={(v) => !v && setCancelTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel Assignment?</AlertDialogTitle>
            <AlertDialogDescription>
              Cancel the assignment for <strong>"{cancelTarget?.title}"</strong>? All assigned users and the assigner will be notified. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep Active</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                if (!cancelTarget) return;
                const { id } = cancelTarget;
                setCancelTarget(null);
                setDetailId(null);
                cancelMut.mutate(id);
              }}
            >
              Cancel Assignment
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Cancel one person's assignment */}
      <AlertDialog open={!!cancelPersonTarget} onOpenChange={(v) => !v && setCancelPersonTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel for {cancelPersonTarget?.userName}?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes the assignment from <strong>{cancelPersonTarget?.userName}</strong> only. They will be told it was cancelled and will not be reminded. Everyone else keeps it.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive hover:bg-destructive/90"
              onClick={() => {
                if (!cancelPersonTarget) return;
                const target = cancelPersonTarget;
                setCancelPersonTarget(null);
                cancelPersonMut.mutate({ assignmentId: target.assignmentId, userId: target.userId });
              }}
            >
              Cancel for this person
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Approve confirm */}
      <AlertDialog open={!!approveTarget} onOpenChange={(v) => !v && setApproveTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Approve Completion?</AlertDialogTitle>
            <AlertDialogDescription>
              Mark <strong>{approveTarget?.userName}</strong>'s submission as approved? They will be notified.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (!approveTarget) return;
                const target = approveTarget;
                setApproveTarget(null);
                approveMut.mutate({ assignmentId: target.assignmentId, userId: target.userId });
              }}
            >
              Approve
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {createOpen && (
        <CreateDocAssignmentSheet
          prefillUserIds={prefillUserIds}
          onClose={() => { setCreateOpen(false); onPrefillUsed?.(); }}
          onCreated={() => { setCreateOpen(false); onPrefillUsed?.(); qc.invalidateQueries({ queryKey: ["admin", "doc-assignments"] }); }}
        />
      )}
    </div>
  );
}

// ── Create Document Assignment sheet ──────────────────────────────────────────
interface AssignableDoc { doc: { id: string; title: string; currentVersionId: string | null; status: string }; category: { name: string } | null; }

export function CreateDocAssignmentSheet({ onClose, onCreated, prefillUserIds }: { onClose: () => void; onCreated: () => void; prefillUserIds?: string[] }) {
  const workersOnly = useIsPlainSupervisor();
  const [docIds, setDocIds] = useState<string[]>([]);
  const [docQuery, setDocQuery] = useState("");
  const [targetMode, setTargetMode] = useState<"all" | "division" | "job_title" | "user" | "sub">(workersOnly || prefillUserIds?.length ? "user" : "all");
  useEffect(() => { if (workersOnly) setTargetMode("user"); }, [workersOnly]);
  const [divisionIds, setDivisionIds] = useState<string[]>([]);
  const [jobTitleIds, setJobTitleIds] = useState<string[]>([]);
  const [userIds, setUserIds] = useState<string[]>(prefillUserIds ?? []);
  const [dueDate, setDueDate] = useState("");
  const [message, setMessage] = useState("");
  const [quizChoice, setQuizChoice] = useState<QuizChoice>(DEFAULT_QUIZ_CHOICE);
  const [submitting, setSubmitting] = useState(false);

  const { data: docsData } = useQuery({
    queryKey: ["admin", "safety-docs", "active"],
    queryFn: () => apiGet<{ data: AssignableDoc[] }>("/api/admin/safety-docs?status=active"),
  });
  const assignableDocs = (docsData?.data ?? []).filter((d) => !!d.doc.currentVersionId);
  const shownDocs = assignableDocs
    .filter((d) => matchesQuery(docQuery, d.doc.title, d.category?.name))
    .sort((a, b) => a.doc.title.localeCompare(b.doc.title, undefined, { sensitivity: "base" }));

  const divisions = useAssignableDivisions(targetMode === "division");

  const { data: jobTitlesData } = useQuery({
    queryKey: ["job-titles"],
    queryFn: () => apiGet<{ data: JobTitle[] }>("/api/job-titles"),
    enabled: targetMode === "job_title",
  });
  const jobTitles = jobTitlesData?.data ?? [];

  const { data: usersData } = useQuery({
    queryKey: ["users-for-notes", workersOnly],
    queryFn: () =>
      workersOnly
        ? // A supervisor picks from their whole crew (workers only, no admins).
          apiGet<{ data: { id: string; name: string }[] }>("/api/supervisor/crew").then((r) => ({
            data: r.data.map((p): UserItem => ({ id: p.id, name: p.name, email: "" })),
          }))
        : apiGet<{ data: UserItem[] }>("/api/users"),
    enabled: targetMode === "user",
  });
  const [userQuery, setUserQuery] = useState("");
  const users = (usersData?.data ?? []).filter((u) => matchesQuery(userQuery, u.name, u.email));

  function toggle(list: string[], setList: (v: string[]) => void, id: string) {
    setList(list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);
  }

  function buildTargets(): { type: string; value?: string | null }[] {
    if (targetMode === "all") return [{ type: "all" }];
    if (targetMode === "division") return divisionIds.map((id) => ({ type: "division", value: id }));
    if (targetMode === "job_title") return jobTitleIds.map((id) => ({ type: "job_title", value: id }));
    return userIds.map((id) => ({ type: "user", value: id }));
  }

  const dup = useDuplicatesPrompt();

  async function handleSubmit() {
    if (docIds.length === 0) { toast.error("Select at least one document"); return; }
    const targets = buildTargets();
    if (targets.length === 0) { toast.error("Select who to assign this to"); return; }

    setSubmitting(true);
    try {
      const documents = assignableDocs
        .filter((d) => docIds.includes(d.doc.id))
        .map((d) => ({ documentId: d.doc.id, versionId: d.doc.currentVersionId! }));
      const body = {
        documents,
        targets,
        dueDate: dueDate || undefined,
        message: message.trim() || undefined,
        ...(({ questionCount, passScore, enableQuiz }) => ({ questionCount, passScore, enableQuiz }))(normalizeQuizChoice(quizChoice)),
      };
      type Made = { created?: number; assignedCount?: number; skippedCount?: number };
      let res: Made;
      try {
        res = await apiPost<Made>("/api/admin/doc-assignments/bulk", body);
      } catch (err) {
        const data = (err as { data?: { code?: string; total?: number; duplicates?: DuplicatePerson[] } }).data;
        if (data?.code !== "duplicates" || !data.duplicates) throw err;
        const choice = await dup.request({ what: documents.length === 1 ? "this document" : "some of these documents", total: data.total ?? 0, duplicates: data.duplicates });
        if (choice === "cancel") return;
        try {
          res = await apiPost<Made>("/api/admin/doc-assignments/bulk", { ...body, duplicates: choice });
        } catch (err2) {
          if ((err2 as { data?: { code?: string } }).data?.code === "all_duplicates") {
            toast.info("Everyone chosen already had it assigned, so nothing new was created.");
            return;
          }
          throw err2;
        }
      }
      toast.success(
        `Assigned ${res.created ?? documents.length} document${(res.created ?? documents.length) !== 1 ? "s" : ""}` +
          ((res.skippedCount ?? 0) > 0 ? `. ${res.skippedCount} already had it assigned and were skipped.` : "")
      );
      onCreated();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to create assignment");
    } finally {
      setSubmitting(false);
    }
  }

  const targetOptions: { id: typeof targetMode; label: string }[] = [
    { id: "all", label: "All Company" },
    { id: "division", label: "Division / Crew" },
    { id: "job_title", label: "Job Title" },
    { id: "user", label: "Specific Workers" },
    { id: "sub", label: "Subcontractors" },
  ].filter((o) => !workersOnly || o.id === "user" || o.id === "sub") as { id: typeof targetMode; label: string }[];

  return (
    <Sheet open onOpenChange={(v) => !v && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Assign Document</SheetTitle>
        </SheetHeader>

        <div className="space-y-5 mt-4">
          <div className="space-y-2">
            <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Document(s)</Label>
            <PickerSearch value={docQuery} onChange={setDocQuery} placeholder="Type to search documents…" />
            <div className="rounded-lg border border-border divide-y max-h-48 overflow-y-auto">
              {shownDocs.map(({ doc, category }) => {
                const checked = docIds.includes(doc.id);
                return (
                  <button
                    key={doc.id}
                    type="button"
                    onClick={() => toggle(docIds, setDocIds, doc.id)}
                    className={cn(
                      "w-full flex items-center justify-between px-3 py-2 text-left transition-colors",
                      checked ? "bg-paris-red/8" : "hover:bg-muted/40"
                    )}
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{doc.title}</p>
                      {category && <p className="text-xs text-muted-foreground truncate">{category.name}</p>}
                    </div>
                    {checked && <CheckCircle2 className="h-4 w-4 text-paris-red shrink-0 ml-2" />}
                  </button>
                );
              })}
              {shownDocs.length === 0 && <p className="text-sm text-muted-foreground px-3 py-4 text-center">{assignableDocs.length === 0 ? "No assignable documents found" : "No document matches your search"}</p>}
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Assign to</Label>
            <div className="grid grid-cols-2 gap-2">
              {targetOptions.map(({ id, label }) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => setTargetMode(id)}
                  className={cn(
                    "px-3 py-2 rounded-lg border text-sm text-left transition-colors",
                    targetMode === id ? "border-paris-red bg-paris-red/10 text-paris-red-text font-medium" : "border-border hover:bg-muted/40"
                  )}
                >
                  {label}
                </button>
              ))}
            </div>

            {targetMode === "division" && (
              <div className="flex flex-wrap gap-2 pt-1">
                {[...divisions].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" })).map((d) => (
                  <button
                    key={d.id}
                    type="button"
                    onClick={() => toggle(divisionIds, setDivisionIds, d.id)}
                    className={cn(
                      "px-3 py-1.5 rounded-full border text-sm transition-colors",
                      divisionIds.includes(d.id) ? "border-paris-red bg-paris-red/10 text-paris-red-text font-medium" : "border-border hover:bg-muted/40 text-muted-foreground"
                    )}
                  >
                    {d.name}
                  </button>
                ))}
              </div>
            )}

            {targetMode === "job_title" && (
              <div className="flex flex-wrap gap-2 pt-1">
                {[...jobTitles].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" })).map((jt) => (
                  <button
                    key={jt.id}
                    type="button"
                    onClick={() => toggle(jobTitleIds, setJobTitleIds, jt.id)}
                    className={cn(
                      "px-3 py-1.5 rounded-full border text-sm transition-colors",
                      jobTitleIds.includes(jt.id) ? "border-paris-red bg-paris-red/10 text-paris-red-text font-medium" : "border-border hover:bg-muted/40 text-muted-foreground"
                    )}
                  >
                    {jt.name}
                  </button>
                ))}
              </div>
            )}

            {targetMode === "sub" && (
              <SubWorkerPicker selected={userIds} onToggle={(id) => toggle(userIds, setUserIds, id)} />
            )}

            {targetMode === "user" && (
              <div className="space-y-2">
              <PickerSearch value={userQuery} onChange={setUserQuery} placeholder="Search workers…" />
              <div className="rounded-lg border border-border divide-y max-h-48 overflow-y-auto">
                {users.length === 0 && <p className="p-3 text-center text-xs text-muted-foreground">No workers found</p>}
                {users.map((u) => (
                  <button
                    key={u.id}
                    type="button"
                    onClick={() => toggle(userIds, setUserIds, u.id)}
                    className={cn(
                      "w-full flex items-center justify-between px-3 py-2 text-left transition-colors",
                      userIds.includes(u.id) ? "bg-paris-red/8" : "hover:bg-muted/40"
                    )}
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{u.name}</p>
                      {u.email && <p className="text-xs text-muted-foreground truncate">{u.email}</p>}
                    </div>
                    {userIds.includes(u.id) && <CheckCircle2 className="h-4 w-4 text-paris-red shrink-0 ml-2" />}
                  </button>
                ))}
              </div>
              </div>
            )}
          </div>

          <QuizAssignmentSettings
            docs={assignableDocs.filter((d) => docIds.includes(d.doc.id)).map((d) => ({ id: d.doc.id, title: d.doc.title }))}
            value={quizChoice}
            onChange={setQuizChoice}
          />

          <div className="space-y-1.5">
            <Label className="text-xs">Due date (optional)</Label>
            <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className="h-9" />
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs">Message (optional)</Label>
            <Textarea value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Any instructions for the crew…" rows={7} />
          </div>

          <Button onClick={handleSubmit} disabled={submitting} className="w-full">
            {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileText className="h-4 w-4 mr-2" />}
            Assign
          </Button>
        </div>
      </SheetContent>
      <DuplicatesPrompt ask={dup.ask} onAnswer={dup.answer} />
    </Sheet>
  );
}

// ── Certification Assignments ─────────────────────────────────────────────────

interface CertType { id: string; name: string; category: string; }
interface Division { id: string; name: string; }
interface JobTitle { id: string; name: string; }
interface UserItem { id: string; name: string; email: string; }

interface CertAssignmentSummary {
  id: string;
  assignedBy?: string;
  certTypeId: string;
  certType: { id: string; name: string } | null;
  assigner: { id: string; name: string } | null;
  assignedAt: string;
  dueDate: string | null;
  message: string | null;
  status: "active" | "cancelled";
  /** The first names of who it is for (the whole count is in stats.total). */
  people?: string[];
  stats: { total: number; assigned: number; submitted: number; approved: number; alreadyValid?: number };
}

interface CertCompletion {
  id: string;
  userId: string;
  workerName: string;
  workerEmail: string;
  status: "assigned" | "submitted" | "approved";
  userCertificationId: string | null;
  claimNote: string | null;
  submittedAt: string | null;
  adminNote: string | null;
  approvedAt: string | null;
  /** Completed by a certification the worker already held (not by an admin). */
  autoMatched?: boolean;
  certExpiry?: string | null;
  certFileId?: string | null;
  certNumber?: string | null;
}

interface CertAssignmentDetail {
  id: string;
  assignedBy?: string;
  certType: { id: string; name: string } | null;
  message: string | null;
  dueDate: string | null;
  status: "active" | "cancelled";
  completions: CertCompletion[];
}

/** The assignment note: long ones fold to a few lines with a "Show full note" switch. */
function NoteBox({ text }: { text: string }) {
  const [open, setOpen] = useState(false);
  const long = text.length > 220 || text.split(String.fromCharCode(10)).length > 5;
  return (
    <div className="mt-2 rounded-lg bg-muted px-3 py-2 text-xs">
      <div className={cn(long && !open && "max-h-24 overflow-hidden")}>
        <MessageText text={text} />
      </div>
      {long && (
        <button type="button" onClick={() => setOpen((v) => !v)} className="mt-1.5 font-medium text-paris-red-text underline underline-offset-2">
          {open ? "Show less" : "Show full note"}
        </button>
      )}
    </div>
  );
}

/** "2028-01-05" as a calendar date (not shifted by the time zone). */
function formatYmd(value: string): string {
  const [y, m, d] = value.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

async function openCertFile(fileId: string) {
  try {
    const res = await apiGet<{ url: string }>(`/api/files/${fileId}/url`);
    window.open(res.url, "_blank", "noopener");
  } catch {
    toast.error("Could not open the certificate");
  }
}

function certCompletionBadge(c: CertCompletion) {
  const s = c.status;
  if (c.autoMatched) {
    return <span className="inline-flex text-[11px] font-medium px-2 py-0.5 rounded-full bg-sky-500/15 text-sky-700 dark:text-sky-400">Already valid</span>;
  }
  const map: Record<CertCompletion["status"], { label: string; cls: string }> = {
    assigned: { label: "Not Started", cls: "bg-orange-500/15 text-orange-700 dark:text-orange-400" },
    submitted: { label: "Awaiting Approval", cls: "bg-blue-500/15 text-blue-700 dark:text-blue-400" },
    approved: { label: "Approved", cls: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400" },
  };
  const v = map[s];
  return <span className={cn("inline-flex text-[11px] font-medium px-2 py-0.5 rounded-full", v.cls)}>{v.label}</span>;
}

function CertAssignmentsView({ prefill, onPrefillUsed }: { prefill?: CertAssignPrefill | null; onPrefillUsed?: () => void } = {}) {
  const { assignmentId: deepLinkId } = useSearch({ strict: false }) as AssignmentsSearch;
  const userFilter = useUserFilter();
  const qc = useQueryClient();
  const [detailId, setDetailId] = useState<string | null>(deepLinkId ?? null);
  useEffect(() => { if (deepLinkId) setDetailId(deepLinkId); }, [deepLinkId]);
  const { data: certSession } = authClient.useSession();
  // Only the assigner or a super admin may cancel an assignment.
  const canCancel = (assignedBy?: string) => certSession?.user?.role === "admin" || (!!assignedBy && assignedBy === certSession?.user?.id);
  const [createOpen, setCreateOpen] = useState(!!prefill);
  const [cleanupOpen, setCleanupOpen] = useState(false);
  const [cancelTarget, setCancelTarget] = useState<{ id: string; name: string } | null>(null);
  const [rejectTarget, setRejectTarget] = useState<{ assignmentId: string; userId: string; userName: string } | null>(null);
  const [rejectNote, setRejectNote] = useState("");
  const [cancelPersonTarget, setCancelPersonTarget] = useState<{ assignmentId: string; userId: string; userName: string } | null>(null);

  const { data: listData, isLoading } = useQuery({
    queryKey: ["admin", "cert-assignments", userFilter.userId],
    queryFn: () =>
      apiGet<{ data: CertAssignmentSummary[] }>(
        `/api/admin/cert-assignments${userFilter.userId ? `?userId=${encodeURIComponent(userFilter.userId)}` : ""}`
      ),
  staleTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
    refetchInterval: 30_000,
  });
  const list = listData?.data ?? [];
  const isDone = (a: { status: string; stats: { total: number; approved: number } }) =>
    a.status === "active" && a.stats.total > 0 && a.stats.approved >= a.stats.total;
  const [showDone, setShowDone] = useState(true);
  const [showCancelled, setShowCancelled] = useState(false);
  const cancelledList = list.filter((a) => a.status === "cancelled");
  const activeList = list.filter((a) => a.status !== "cancelled" && !isDone(a));
  const doneList = list.filter(isDone);
  const visibleList = [...activeList, ...(showDone ? doneList : []), ...(showCancelled ? cancelledList : [])];
  const firstDoneId = doneList[0]?.id;
  const firstCancelledId = cancelledList[0]?.id;

  const { data: detailData } = useQuery({
    queryKey: ["admin", "cert-assignment-detail", detailId],
    queryFn: () => apiGet<{ data: CertAssignmentDetail }>(`/api/admin/cert-assignments/${detailId}`),
    staleTime: 0,
    refetchOnMount: "always",
    enabled: !!detailId,
  });
  const detail = detailData?.data ?? null;

  const cancelMut = useMutation({
    mutationFn: (id: string) => apiPatch(`/api/admin/cert-assignments/${id}/cancel`, {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "cert-assignments"] });
      toast.success("Assignment cancelled");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const approveMut = useMutation({
    mutationFn: ({ assignmentId, userId }: { assignmentId: string; userId: string }) =>
      apiPatch(`/api/admin/cert-assignments/${assignmentId}/completions/${userId}/approve`, {}),
    onSuccess: () => {
      if (detailId) qc.invalidateQueries({ queryKey: ["admin", "cert-assignment-detail", detailId] });
      qc.invalidateQueries({ queryKey: ["admin", "cert-assignments"] });
      toast.success("Certification approved");
    },
    onError: (e: Error) => {
      toast.error(e.message);
      // Someone may already have approved it: show the real state.
      if (detailId) qc.invalidateQueries({ queryKey: ["admin", "cert-assignment-detail", detailId] });
      qc.invalidateQueries({ queryKey: ["admin", "cert-assignments"] });
    },
  });

  const cancelPersonMut = useMutation({
    mutationFn: ({ assignmentId, userId }: { assignmentId: string; userId: string }) =>
      apiPatch(`/api/admin/cert-assignments/${assignmentId}/completions/${userId}/cancel`, {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "cert-assignment-detail"] });
      qc.invalidateQueries({ queryKey: ["admin", "cert-assignments"] });
      toast.success("Assignment cancelled for this person");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const rejectMut = useMutation({
    mutationFn: ({ assignmentId, userId, adminNote }: { assignmentId: string; userId: string; adminNote: string }) =>
      apiPatch(`/api/admin/cert-assignments/${assignmentId}/completions/${userId}/reject`, { adminNote }),
    onSuccess: () => {
      if (detailId) qc.invalidateQueries({ queryKey: ["admin", "cert-assignment-detail", detailId] });
      qc.invalidateQueries({ queryKey: ["admin", "cert-assignments"] });
      toast.success("Sent back to the worker");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-end gap-2">
        {certSession?.user?.role === "admin" && (
          <Button size="sm" variant="outline" onClick={() => setCleanupOpen(true)}>
            <Copy className="h-4 w-4 mr-1.5" /> Clean up duplicates
          </Button>
        )}
        <Button size="sm" onClick={() => setCreateOpen(true)}>
          <Award className="h-4 w-4 mr-1.5" /> Assign Certification
        </Button>
      </div>
      {cleanupOpen && <DuplicatesCleanupDialog basePath="/api/admin/cert-assignments" queryKey={["admin", "cert-assignments"]} onClose={() => setCleanupOpen(false)} onDone={() => qc.invalidateQueries({ queryKey: ["admin", "cert-assignments"] })} />}

      {isLoading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
      ) : list.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center py-12 text-center text-muted-foreground">
            <Award className="h-10 w-10 mb-3 opacity-20" />
            <p className="text-sm">No certification assignments yet</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {visibleList.map((a) => {
            const pct = a.stats.total > 0 ? Math.round((a.stats.approved / a.stats.total) * 100) : 0;
            const needsAttention = a.status === "active" && a.stats.submitted > 0;
            return (
              <Fragment key={a.id}>
              {a.id === firstCancelledId && (
                <button
                  type="button"
                  onClick={() => setShowCancelled((v) => !v)}
                  className="flex w-full items-center justify-between pt-5 pb-1 text-left"
                  aria-expanded={showCancelled}
                >
                  <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Cancelled ({cancelledList.length})</span>
                  <span className="text-xs text-muted-foreground">Hide</span>
                </button>
              )}
              {a.id === firstDoneId && (
                <button
                  type="button"
                  onClick={() => setShowDone((v) => !v)}
                  className="flex w-full items-center justify-between pt-5 pb-1 text-left"
                  aria-expanded={showDone}
                >
                  <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Completed ({doneList.length})</span>
                  <span className="text-xs text-muted-foreground">{showDone ? "Hide" : "Show"}</span>
                </button>
              )}
              <Card
                key={a.id}
                className={cn(
                  "cursor-pointer transition-colors",
                  needsAttention ? "border-orange-400/60 bg-orange-500/5 hover:bg-orange-500/10" : "hover:bg-muted/30"
                )}
                onClick={() => setDetailId(a.id)}
              >
                <CardContent className="px-4 py-3">
                  <div className="flex items-start gap-3">
                    <Award className={cn("h-5 w-5 mt-0.5 shrink-0", needsAttention ? "text-orange-500/70" : "text-muted-foreground/50")} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-sm font-medium truncate">{a.certType?.name ?? "Certification"}</p>
                        {a.status === "cancelled" && <Badge variant="secondary">Cancelled</Badge>}
                        {a.stats.submitted > 0 && (
                          <span className="inline-flex items-center gap-1 text-[10px] font-bold bg-amber-500/15 text-amber-600 dark:text-amber-400 px-1.5 py-0.5 rounded-full">
                            <Clock className="h-2.5 w-2.5" />{a.stats.submitted} Awaiting
                          </span>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1">
                        <p className="text-xs text-muted-foreground">
                          By {a.assigner?.name ?? "—"} · {new Date(a.assignedAt).toLocaleDateString()}
                        </p>
                        {a.dueDate && (
                          <p className="text-xs text-muted-foreground flex items-center gap-1">
                            <CalendarIcon className="h-3 w-3" /> Due {formatDateOnly(a.dueDate)}
                          </p>
                        )}
                        {a.people && a.people.length > 0 && (
                          <p className="text-xs text-muted-foreground flex items-center gap-1">
                            <Users className="h-3 w-3 shrink-0" />
                            {a.people.join(", ")}
                            {a.stats.total > a.people.length ? ` +${a.stats.total - a.people.length} more` : ""}
                          </p>
                        )}
                      </div>
                      {a.message && <p className="text-xs text-muted-foreground italic mt-1 truncate">"{a.message}"</p>}
                      {a.stats.total > 0 && (
                        <div className="mt-2 flex items-center gap-2">
                          <div className="flex-1 h-1.5 rounded-full bg-muted overflow-hidden">
                            <div className="h-full rounded-full bg-emerald-500 transition-all" style={{ width: `${pct}%` }} />
                          </div>
                          <span className="text-[11px] text-muted-foreground whitespace-nowrap">
                            {a.stats.approved}/{a.stats.total} approved · {a.stats.submitted} awaiting{a.stats.alreadyValid ? ` · ${a.stats.alreadyValid} already valid` : ""}
                          </span>
                        </div>
                      )}
                    </div>
                    {a.status === "active" && a.stats.approved < a.stats.total && canCancel(a.assignedBy) && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0 text-destructive hover:text-destructive shrink-0"
                        title="Cancel assignment"
                        onClick={(e) => { e.stopPropagation(); setCancelTarget({ id: a.id, name: a.certType?.name ?? "this certification" }); }}
                      >
                        <Ban className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
              </Fragment>
            );
          })}
          {doneList.length > 0 && !showDone && (
            <button
              type="button"
              onClick={() => setShowDone(true)}
              className="flex w-full items-center justify-between pt-5 pb-1 text-left"
            >
              <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Completed ({doneList.length})</span>
              <span className="text-xs text-muted-foreground">Show</span>
            </button>
          )}
          {cancelledList.length > 0 && !showCancelled && (
            <button
              type="button"
              onClick={() => setShowCancelled(true)}
              className="flex w-full items-center justify-between pt-5 pb-1 text-left"
            >
              <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Cancelled ({cancelledList.length})</span>
              <span className="text-xs text-muted-foreground">Show</span>
            </button>
          )}
        </div>
      )}

      {/* Detail sheet */}
      {detailId && (
        <Sheet open onOpenChange={(v) => !v && setDetailId(null)}>
          <SheetContent side="right" className="w-full sm:max-w-lg flex flex-col">
            {!detail ? (
              <div className="flex-1 flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
            ) : (
              <>
                <div className="flex-1 min-h-0 overflow-y-auto pr-1">
                <SheetHeader>
                  <SheetTitle className="truncate">{detail.certType?.name ?? "Certification"}</SheetTitle>
                  {detail.dueDate && (
                    <p className="text-xs text-muted-foreground">Due {formatDateOnly(detail.dueDate)}</p>
                  )}
                </SheetHeader>
                {detail.message && <NoteBox text={detail.message} />}

                <div className="flex items-center justify-between mt-4 mb-1">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Crew ({detail.completions.length})</p>
                </div>
                <div className="space-y-2">
                  {detail.completions.map((c) => (
                    <div key={c.userId} className="py-2 border-b border-border/40 last:border-0 space-y-1.5">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{c.workerName}</p>
                          <p className="text-[11px] text-muted-foreground">{c.workerEmail}</p>
                        </div>
                        {certCompletionBadge(c)}
                        {(c.status === "assigned" || c.status === "submitted") && detail.status === "active" && canCancel(detail.assignedBy) && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-6 text-[11px] px-2 text-destructive border-destructive/40 hover:bg-destructive/10"
                            title="Cancel this assignment for this person only"
                            onClick={() => setCancelPersonTarget({ assignmentId: detail.id, userId: c.userId, userName: c.workerName })}
                            disabled={cancelPersonMut.isPending}
                          >
                            Cancel
                          </Button>
                        )}
                      </div>
                      {c.claimNote && (
                        <p className="text-[11px] text-muted-foreground italic flex items-start gap-1">
                          <StickyNote className="h-3 w-3 mt-0.5 shrink-0" />
                          "I already have this — {c.claimNote}"
                        </p>
                      )}
                      {c.status === "submitted" && !c.claimNote && (
                        <p className="text-[11px] text-muted-foreground italic">Uploaded a new certification for review.</p>
                      )}
                      {c.autoMatched && (
                        <>
                          <p className="text-[11px] text-muted-foreground">
                            {c.certExpiry
                              ? `Already valid until ${formatYmd(c.certExpiry)}`
                              : "Already valid (no expiry date)"}
                            {c.certNumber ? ` · #${c.certNumber}` : ""} · not assigned or reminded
                          </p>
                          <div className="flex items-center gap-2">
                            {c.certFileId ? (
                              <Button size="sm" variant="outline" className="h-6 text-[11px] px-2" onClick={() => openCertFile(c.certFileId!)}>
                                View certificate
                              </Button>
                            ) : (
                              <Link
                                to="/admin/certifications"
                                search={{ q: c.workerName, assignmentId: detail.id, workerId: c.userId }}
                                className="text-[11px] font-medium px-2.5 py-1 rounded-full ring-1 ring-border text-muted-foreground hover:bg-muted transition-colors"
                              >
                                View Crew's Certs
                              </Link>
                            )}
                            {/* Already counted as done: there is nothing to approve. Send back only if the certificate looks wrong. */}
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-6 text-[11px] px-2 text-destructive border-destructive/40 hover:bg-destructive/10"
                              title="Ask the worker to upload it again"
                              onClick={() => { setRejectTarget({ assignmentId: detail.id, userId: c.userId, userName: c.workerName }); setRejectNote(""); }}
                              disabled={rejectMut.isPending}
                            >
                              Send back
                            </Button>
                          </div>
                        </>
                      )}
                      {c.status === "submitted" && (
                        <div className="flex items-center gap-2">
                          <Link
                            to="/admin/certifications"
                            search={{ q: c.workerName, assignmentId: detail.id, workerId: c.userId }}
                            className="text-[11px] font-medium px-2.5 py-1 rounded-full ring-1 ring-border text-muted-foreground hover:bg-muted transition-colors"
                          >
                            View Crew's Certs
                          </Link>
                          <Button size="sm" className="h-6 text-[11px] px-2" onClick={() => approveMut.mutate({ assignmentId: detail.id, userId: c.userId })} disabled={approveMut.isPending}>
                            Approve
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-6 text-[11px] px-2 text-destructive border-destructive/40 hover:bg-destructive/10"
                            onClick={() => { setRejectTarget({ assignmentId: detail.id, userId: c.userId, userName: c.workerName }); setRejectNote(""); }}
                            disabled={rejectMut.isPending}
                          >
                            Reject
                          </Button>
                        </div>
                      )}
                      {c.adminNote && c.status !== "submitted" && !c.autoMatched && (
                        <p className="text-[11px] text-muted-foreground">Note: {c.adminNote}</p>
                      )}
                    </div>
                  ))}
                  {detail.completions.length === 0 && (
                    <p className="text-sm text-muted-foreground text-center py-8">No one assigned yet</p>
                  )}
                </div>

                </div>

                {detail.status === "active" && detail.completions.some((c) => c.status !== "approved") && canCancel(detail.assignedBy) && (
                  <div className="pt-4 border-t">
                    <Button
                      variant="destructive"
                      size="sm"
                      className="w-full"
                      onClick={() => setCancelTarget({ id: detail.id, name: detail.certType?.name ?? "this certification" })}
                      disabled={cancelMut.isPending}
                    >
                      <Ban className="h-4 w-4 mr-2" /> Cancel Assignment
                    </Button>
                  </div>
                )}
              </>
            )}
          </SheetContent>
        </Sheet>
      )}

      {createOpen && (
        <CreateCertAssignmentSheet
          prefill={prefill}
          onClose={() => { setCreateOpen(false); onPrefillUsed?.(); }}
          onCreated={() => { setCreateOpen(false); onPrefillUsed?.(); qc.invalidateQueries({ queryKey: ["admin", "cert-assignments"] }); }}
        />
      )}

      {/* Cancel confirm */}
      <AlertDialog open={!!cancelTarget} onOpenChange={(v) => !v && setCancelTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel Assignment?</AlertDialogTitle>
            <AlertDialogDescription>
              Cancel the <strong>"{cancelTarget?.name}"</strong> assignment? This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep Active</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                if (!cancelTarget) return;
                const { id } = cancelTarget;
                setCancelTarget(null);
                setDetailId(null);
                cancelMut.mutate(id);
              }}
            >
              Cancel Assignment
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reject with note */}
      <AlertDialog open={!!cancelPersonTarget} onOpenChange={(v) => !v && setCancelPersonTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel for {cancelPersonTarget?.userName}?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes the assignment from <strong>{cancelPersonTarget?.userName}</strong> only. They will be told it was cancelled and will not be reminded. Everyone else keeps it.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive hover:bg-destructive/90"
              onClick={() => {
                if (!cancelPersonTarget) return;
                const target = cancelPersonTarget;
                setCancelPersonTarget(null);
                cancelPersonMut.mutate({ assignmentId: target.assignmentId, userId: target.userId });
              }}
            >
              Cancel for this person
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!rejectTarget} onOpenChange={(v) => !v && setRejectTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Send back to {rejectTarget?.userName}?</AlertDialogTitle>
            <AlertDialogDescription>
              Explain what's wrong so they know what to fix or re-upload.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Textarea
            value={rejectNote}
            onChange={(e) => setRejectNote(e.target.value)}
            placeholder="e.g. The file is illegible, please re-upload a clearer photo."
            rows={3}
          />
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={!rejectNote.trim()}
              onClick={() => {
                if (!rejectTarget) return;
                const target = rejectTarget;
                setRejectTarget(null);
                rejectMut.mutate({ assignmentId: target.assignmentId, userId: target.userId, adminNote: rejectNote.trim() });
              }}
            >
              Send Back
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

/** One person who already has what is being assigned. */
interface DuplicatePerson {
  userId: string;
  name: string;
  dueDate: string | null;
  assignedByName: string | null;
  /** Which document, when several are assigned together. */
  title?: string;
  /** Already approved (finished), not only open. */
  done?: boolean;
}
type DuplicatesAsk = { what: string; total: number; duplicates: DuplicatePerson[]; resolve: (choice: "skip" | "include" | "cancel") => void };

/** Asks what to do with people who already have this assigned: skip them, assign again anyway, or cancel. */
function DuplicatesPrompt({ ask, onAnswer }: { ask: DuplicatesAsk | null; onAnswer: (choice: "skip" | "include" | "cancel") => void }) {
  return (
    <AlertDialog open={!!ask} onOpenChange={(o) => !o && onAnswer("cancel")}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Already assigned</AlertDialogTitle>
          <AlertDialogDescription>
            {new Set(ask?.duplicates.map((d) => d.userId)).size} of {ask?.total} {ask?.total === 1 ? "person already has" : "people already have"} {ask?.what} assigned. Assigning it again makes two assignments for each of them.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <ul className="max-h-48 space-y-1 overflow-y-auto rounded-lg border p-2 text-sm">
          {ask?.duplicates.map((d, i) => (
            <li key={`${d.userId}-${i}`} className="flex items-baseline justify-between gap-2">
              <span className="min-w-0">
                <span className="font-medium">{d.name}</span>
                {d.title ? <span className="text-muted-foreground"> · {d.title}</span> : null}
              </span>
              <span className="shrink-0 truncate text-xs text-muted-foreground">
                {d.done ? "approved" : "open"}
                {d.assignedByName ? ` · by ${d.assignedByName}` : ""}
                {d.dueDate ? ` · due ${formatDateOnly(d.dueDate)}` : ""}
              </span>
            </li>
          ))}
        </ul>
        <AlertDialogFooter className="gap-2 sm:flex-col sm:space-x-0">
          <Button type="button" onClick={() => onAnswer("skip")}>Skip them (recommended)</Button>
          <Button type="button" variant="outline" onClick={() => onAnswer("include")}>Assign again anyway</Button>
          <Button type="button" variant="ghost" onClick={() => onAnswer("cancel")}>Cancel</Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

/** Open assignments the person already has done elsewhere (approved in another assignment, or a valid certificate): preview, then close them. */
function DuplicatesCleanupDialog({ basePath, queryKey, onClose, onDone }: { basePath: string; queryKey: string[]; onClose: () => void; onDone: () => void }) {
  const { data, isLoading } = useQuery({
    queryKey: [...queryKey, "duplicates"],
    queryFn: () => apiGet<{ data: { completionId: string; userName: string; title: string | null; assignedByName: string | null; reason: string }[] }>(`${basePath}/duplicates`),
    staleTime: 0,
  });
  const rows = data?.data ?? [];
  const close = useMutation({
    mutationFn: () => apiPost<{ closed: number }>(`${basePath}/duplicates/close`, {}),
    onSuccess: (r) => {
      toast.success(`${r.closed} assignment${r.closed !== 1 ? "s" : ""} closed`);
      onDone();
      onClose();
    },
    onError: (e: Error) => toast.error(e.message),
  });
  return (
    <AlertDialog open onOpenChange={(o) => !o && onClose()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Clean up duplicates</AlertDialogTitle>
          <AlertDialogDescription>
            These are assigned and not finished, but the person already has them done: approved in another assignment, or (for certifications) a valid certificate with more than 30 days left. Closing marks each as done.
          </AlertDialogDescription>
        </AlertDialogHeader>
        {isLoading ? (
          <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
        ) : rows.length === 0 ? (
          <p className="py-4 text-center text-sm text-muted-foreground">No duplicates found.</p>
        ) : (
          <ul className="max-h-60 space-y-1 overflow-y-auto rounded-lg border p-2 text-sm">
            {rows.map((r) => (
              <li key={r.completionId}>
                <span className="font-medium">{r.userName}</span> · {r.title ?? "Assignment"}
                <span className="block truncate text-xs text-muted-foreground">{r.assignedByName ? `Assigned by ${r.assignedByName} · ` : ""}{r.reason}</span>
              </li>
            ))}
          </ul>
        )}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={close.isPending}>{rows.length === 0 ? "Close" : "Cancel"}</AlertDialogCancel>
          {rows.length > 0 && (
            <Button type="button" disabled={close.isPending} onClick={() => close.mutate()}>
              {close.isPending ? "Closing..." : `Close ${rows.length} assignment${rows.length !== 1 ? "s" : ""}`}
            </Button>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

/** Keeps the prompt's answer for the submit code to wait on. */
function useDuplicatesPrompt() {
  const [ask, setAsk] = useState<DuplicatesAsk | null>(null);
  const request = (info: Omit<DuplicatesAsk, "resolve">) => new Promise<"skip" | "include" | "cancel">((resolve) => setAsk({ ...info, resolve }));
  const answer = (choice: "skip" | "include" | "cancel") => {
    ask?.resolve(choice);
    setAsk(null);
  };
  return { ask, request, answer };
}

// ── Create Certification Assignment sheet ─────────────────────────────────────
export function CreateCertAssignmentSheet({ onClose, onCreated, prefill }: { onClose: () => void; onCreated: () => void; prefill?: CertAssignPrefill | null }) {
  const workersOnly = useIsPlainSupervisor();
  const [certTypeIds, setCertTypeIds] = useState<string[]>(prefill?.certTypeId ? [prefill.certTypeId] : []);
  const [certQuery, setCertQuery] = useState("");
  const [targetMode, setTargetMode] = useState<"all" | "division" | "job_title" | "user" | "sub">(workersOnly || prefill ? "user" : "all");
  useEffect(() => { if (workersOnly) setTargetMode("user"); }, [workersOnly]);
  const [divisionIds, setDivisionIds] = useState<string[]>([]);
  const [jobTitleIds, setJobTitleIds] = useState<string[]>([]);
  const [userIds, setUserIds] = useState<string[]>(prefill?.workerIds ?? []);
  const [dueDate, setDueDate] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  // People who already have this certification assigned and not finished: ask what to do before assigning it a second time
  const dup = useDuplicatesPrompt();

  const { data: certTypesData } = useQuery({
    queryKey: ["certifications", "types"],
    queryFn: () => apiGet<{ data: CertType[] }>("/api/certifications/types"),
  });
  const certTypes = certTypesData?.data ?? [];
  const shownCertTypes = certTypes
    .filter((ct) => matchesQuery(certQuery, ct.name))
    .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));

  const divisions = useAssignableDivisions(targetMode === "division");

  const { data: jobTitlesData } = useQuery({
    queryKey: ["job-titles"],
    queryFn: () => apiGet<{ data: JobTitle[] }>("/api/job-titles"),
    enabled: targetMode === "job_title",
  });
  const jobTitles = jobTitlesData?.data ?? [];

  const { data: usersData } = useQuery({
    queryKey: ["users-for-notes", workersOnly],
    queryFn: () =>
      workersOnly
        ? // A supervisor picks from their whole crew (workers only, no admins).
          apiGet<{ data: { id: string; name: string }[] }>("/api/supervisor/crew").then((r) => ({
            data: r.data.map((p): UserItem => ({ id: p.id, name: p.name, email: "" })),
          }))
        : apiGet<{ data: UserItem[] }>("/api/users"),
    enabled: targetMode === "user",
  });
  const [userQuery, setUserQuery] = useState("");
  const users = (usersData?.data ?? []).filter((u) => matchesQuery(userQuery, u.name, u.email));

  function toggle(list: string[], setList: (v: string[]) => void, id: string) {
    setList(list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);
  }

  function buildTargets(): { type: string; value?: string | null }[] {
    if (targetMode === "all") return [{ type: "all" }];
    if (targetMode === "division") return divisionIds.map((id) => ({ type: "division", value: id }));
    if (targetMode === "job_title") return jobTitleIds.map((id) => ({ type: "job_title", value: id }));
    return userIds.map((id) => ({ type: "user", value: id }));
  }

  async function handleSubmit() {
    if (certTypeIds.length === 0) { toast.error("Select at least one certification"); return; }
    const targets = buildTargets();
    if (targets.length === 0) { toast.error("Select who to assign this to"); return; }

    setSubmitting(true);
    try {
      let assignedTotal = 0;
      let alreadyValidTotal = 0;
      let skippedTotal = 0;
      type Made = { assignedCount: number; alreadyValidCount?: number; skippedCount?: number };
      for (const certTypeId of certTypeIds) {
        const body = { certTypeId, targets, dueDate: dueDate || undefined, message: message.trim() || undefined };
        let res: Made | null = null;
        try {
          res = await apiPost<Made>("/api/admin/cert-assignments", body);
        } catch (err) {
          const data = (err as { data?: { code?: string; title?: string; total?: number; duplicates?: DuplicatePerson[] } }).data;
          if (data?.code === "duplicates" && data.duplicates) {
            const choice = await dup.request({ what: `"${data.title ?? "this certification"}"`, total: data.total ?? 0, duplicates: data.duplicates });
            if (choice === "cancel") return;
            try {
              res = await apiPost<Made>("/api/admin/cert-assignments", { ...body, duplicates: choice });
            } catch (err2) {
              // Everyone chosen already had it open and they were skipped: nothing to create for this one
              if ((err2 as { data?: { code?: string } }).data?.code === "all_duplicates") skippedTotal += data.duplicates.length;
              else throw err2;
            }
          } else throw err;
        }
        if (res) {
          assignedTotal += res.assignedCount ?? 0;
          alreadyValidTotal += res.alreadyValidCount ?? 0;
          skippedTotal += res.skippedCount ?? 0;
        }
      }
      toast.success(
        `Assigned ${certTypeIds.length} certification${certTypeIds.length !== 1 ? "s" : ""} to ${assignedTotal} worker${assignedTotal !== 1 ? "s" : ""}` +
          (alreadyValidTotal > 0 ? `. ${alreadyValidTotal} already had a valid one (more than 30 days left), so they were not assigned.` : "") +
          (skippedTotal > 0 ? `. ${skippedTotal} already had it assigned and were skipped.` : "")
      );
      onCreated();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to create assignment");
    } finally {
      setSubmitting(false);
    }
  }

  const targetOptions: { id: typeof targetMode; label: string; icon: typeof Users }[] = [
    { id: "all", label: "All Company", icon: Users },
    { id: "division", label: "Division / Crew", icon: Users },
    { id: "job_title", label: "Job Title", icon: Users },
    { id: "user", label: "Specific Workers", icon: Users },
    { id: "sub", label: "Subcontractors", icon: Users },
  ].filter((o) => !workersOnly || o.id === "user" || o.id === "sub") as { id: typeof targetMode; label: string; icon: typeof Users }[];

  return (
    <Sheet open onOpenChange={(v) => !v && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Assign Certification</SheetTitle>
        </SheetHeader>

        <div className="space-y-5 mt-4">
          <div className="space-y-2">
            <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Certification(s)</Label>
            <PickerSearch value={certQuery} onChange={setCertQuery} placeholder="Type to search certifications…" />
            <div className="rounded-lg border border-border divide-y max-h-48 overflow-y-auto">
              {shownCertTypes.map((ct) => {
                const checked = certTypeIds.includes(ct.id);
                return (
                  <button
                    key={ct.id}
                    type="button"
                    onClick={() => toggle(certTypeIds, setCertTypeIds, ct.id)}
                    className={cn(
                      "w-full flex items-center justify-between px-3 py-2 text-left text-sm transition-colors",
                      checked ? "bg-paris-red/8" : "hover:bg-muted/40"
                    )}
                  >
                    {ct.name}
                    {checked && <CheckCircle2 className="h-4 w-4 text-paris-red shrink-0" />}
                  </button>
                );
              })}
              {shownCertTypes.length === 0 && <p className="text-sm text-muted-foreground px-3 py-4 text-center">{certTypes.length === 0 ? "No certification types found" : "No certification matches your search"}</p>}
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Assign to</Label>
            <div className="grid grid-cols-2 gap-2">
              {targetOptions.map(({ id, label }) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => setTargetMode(id)}
                  className={cn(
                    "px-3 py-2 rounded-lg border text-sm text-left transition-colors",
                    targetMode === id ? "border-paris-red bg-paris-red/10 text-paris-red-text font-medium" : "border-border hover:bg-muted/40"
                  )}
                >
                  {label}
                </button>
              ))}
            </div>

            {targetMode === "division" && (
              <div className="flex flex-wrap gap-2 pt-1">
                {[...divisions].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" })).map((d) => (
                  <button
                    key={d.id}
                    type="button"
                    onClick={() => toggle(divisionIds, setDivisionIds, d.id)}
                    className={cn(
                      "px-3 py-1.5 rounded-full border text-sm transition-colors",
                      divisionIds.includes(d.id) ? "border-paris-red bg-paris-red/10 text-paris-red-text font-medium" : "border-border hover:bg-muted/40 text-muted-foreground"
                    )}
                  >
                    {d.name}
                  </button>
                ))}
              </div>
            )}

            {targetMode === "job_title" && (
              <div className="flex flex-wrap gap-2 pt-1">
                {[...jobTitles].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" })).map((jt) => (
                  <button
                    key={jt.id}
                    type="button"
                    onClick={() => toggle(jobTitleIds, setJobTitleIds, jt.id)}
                    className={cn(
                      "px-3 py-1.5 rounded-full border text-sm transition-colors",
                      jobTitleIds.includes(jt.id) ? "border-paris-red bg-paris-red/10 text-paris-red-text font-medium" : "border-border hover:bg-muted/40 text-muted-foreground"
                    )}
                  >
                    {jt.name}
                  </button>
                ))}
              </div>
            )}

            {targetMode === "sub" && (
              <SubWorkerPicker selected={userIds} onToggle={(id) => toggle(userIds, setUserIds, id)} />
            )}

            {targetMode === "user" && (
              <div className="space-y-2">
              <PickerSearch value={userQuery} onChange={setUserQuery} placeholder="Search workers…" />
              <div className="rounded-lg border border-border divide-y max-h-48 overflow-y-auto">
                {users.length === 0 && <p className="p-3 text-center text-xs text-muted-foreground">No workers found</p>}
                {users.map((u) => (
                  <button
                    key={u.id}
                    type="button"
                    onClick={() => toggle(userIds, setUserIds, u.id)}
                    className={cn(
                      "w-full flex items-center justify-between px-3 py-2 text-left transition-colors",
                      userIds.includes(u.id) ? "bg-paris-red/8" : "hover:bg-muted/40"
                    )}
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{u.name}</p>
                      {u.email && <p className="text-xs text-muted-foreground truncate">{u.email}</p>}
                    </div>
                    {userIds.includes(u.id) && <CheckCircle2 className="h-4 w-4 text-paris-red shrink-0 ml-2" />}
                  </button>
                ))}
              </div>
              </div>
            )}
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs">Due date (optional)</Label>
            <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className="h-9" />
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs">Message (optional)</Label>
            <Textarea value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Any instructions for the crew…" rows={7} />
          </div>

          <Button onClick={handleSubmit} disabled={submitting} className="w-full">
            {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Award className="h-4 w-4 mr-2" />}
            Assign
          </Button>
        </div>
      </SheetContent>
      <DuplicatesPrompt ask={dup.ask} onAnswer={dup.answer} />
    </Sheet>
  );
}
